import { useState } from 'react';
import { Link } from 'react-router';
import { Meta } from '@/components/common';
import { Container } from '@/components/layout';
import { ROUTES } from '@/constants';
import { cn } from '@/lib/utils';

/**
 * The user manual, laid out as the design draws it.
 *
 * A 260px contents rail beside a single document card, 28px apart. The rail is
 * sticky; the card is 12px radius at 32px/38px with prose capped at 62
 * characters a line - which is the measurement worth keeping, because it is
 * what makes a wall of instructions readable, and it is the first thing lost
 * when a page is built full-width.
 *
 * ── Why one section at a time ───────────────────────────────────────────────
 *
 * Nobody reads a manual. They arrive stuck at one step and need to find that
 * step, which is why the contents is always visible and why each section is
 * self-contained. A single scrolling page hides the structure exactly when
 * somebody needs it most.
 *
 * The content is static and lives here rather than in a CMS. It changes when
 * the software changes - which is a release, and a release already deploys this
 * portal.
 */
interface Step {
  body: string;
  /** The thing somebody would otherwise learn by getting it wrong once. */
  note?: string;
}

interface Section {
  id: string;
  number: number;
  title: string;
  /** The mono eyebrow above the heading: "SECTION 3 · LICENSING". */
  kicker: string;
  summary: string;
  before?: string;
  steps: Step[];
}

// Written from how the product behaves (2026-11-03): the seat_assigned email, the
// Dashboard downloads (Windows web setup, Linux .tar.gz), the desktop's first-run
// steps (activate, sign in, PIN), the NμCAD steps, Send for Review, the 7-day
// offline grant, Settings > Updates, and seats released by support. The FAQ
// (routes/faq) answers the same questions in short.
const SECTIONS: Section[] = [
  {
    id: 'welcome',
    number: 1,
    title: 'Your welcome email and first sign-in',
    kicker: 'Getting started',
    summary:
      'When your lab gives you a seat, NμDent emails you everything you need. This is what is in it and what to do with it.',
    before:
      'Look for an email from NμDent with the subject "Your NμDent licence key and sign-in details". It has your sign-in email, a password, your licence key and a "Sign in to the NμDent portal" button. Keep it: you need the licence key once on each computer you set up. Not there? Check your spam folder, then ask your lab owner to check the email address on your seat.',
    steps: [
      {
        body: 'Choose "Sign in to the NμDent portal" in the email - it opens this portal - and sign in with the email and password from the email.',
      },
      {
        body: 'Change the password straight away under Profile > Change password. The new one is the password for this portal and for NμDent on every computer.',
        note: 'If your lab owner chose your password for you, the email has no password in it - ask them for it.',
      },
      {
        body: 'Open the Dashboard. Software version shows the newest NμDent and its Download button. Continue with section 2 to install it.',
      },
      {
        body: "Other emails you will get from NμDent: the dentist's decision on each crown you send for review, licence reminders 60, 30 and 7 days before it expires, a note when a new version is out (if switched on in Settings), and every reply to an issue you raise.",
      },
    ],
  },
  {
    id: 'install',
    number: 2,
    title: 'Installing NμDent',
    kicker: 'Installation',
    summary:
      'Getting NμDent and NμCAD onto the computer that will do the designing, and setting it up the first time.',
    before:
      'Install on the computer with the graphics card that will do the designing, not through a remote desktop session. Each computer you set up takes one seat of your licence.',
    steps: [
      {
        body: 'Windows: on the Dashboard choose Download and run NuDent-Setup. The setup is small and fetches the rest of NμDent (about 500 MB) while it installs, so keep the computer online until it finishes.',
        note: 'Windows may warn about an unrecognised publisher. Choose More info, then Run anyway.',
      },
      {
        body: 'Linux: on the Dashboard choose "Also for Linux". Unpack the download (tar -xzf NuDent-<version>-linux-x86_64.tar.gz) and start NμDent with ./NuDent/NuDent.',
      },
      {
        body: 'Open NμDent. First, Activate this computer: enter your email and the licence key from your welcome email.',
      },
      {
        body: 'Then Sign in with the same email and password you use for this portal.',
      },
      {
        body: 'Finally, choose a 4-digit PIN. NμDent asks for it when there is no internet connection, so you can keep working offline for up to 7 days.',
        note: 'Closing NμDent part-way through is fine: it starts again from the step you stopped at.',
      },
    ],
  },
  {
    id: 'scans',
    number: 3,
    title: 'Starting a case and importing scans',
    kicker: 'Scans',
    summary: 'Getting the intraoral scans into a case.',
    steps: [
      {
        body: 'Create a new case and add the scans: upper arch, lower arch and bite. STL, PLY and OBJ files are all accepted, from any scanner - there is nothing to configure per device.',
      },
      {
        body: 'Open the case in NμCAD to start designing. The case and its scans are backed up to the cloud as you work.',
      },
    ],
  },
  {
    id: 'design',
    number: 4,
    title: 'Designing a crown in NμCAD',
    kicker: 'Design',
    summary: 'Six steps from the scans to a finished crown.',
    steps: [
      { body: 'Load scans: the case scans open in NμCAD.' },
      {
        body: 'AI margin: one click and the margin line is drawn on the prep. Check it, and adjust it where it needs it.',
        note: 'The margin is the one step worth slowing down on. Everything after it is fitted to it, and a margin placed two tenths of a millimetre out is a remake.',
      },
      { body: 'Cement gap: set the space left for cement between the prep and the crown.' },
      {
        body: 'Crown placement: pick a crown from the library of all 32 teeth and it is seated on the prep.',
      },
      {
        body: 'Refine: the contact heatmap shows where the crown touches its neighbours. Adjust contacts and occlusion on the model.',
      },
      {
        body: 'Export: save the crown as STL for your mill or printer, or as a 3D PDF with a section for the doctor.',
      },
    ],
  },
  {
    id: 'review',
    number: 5,
    title: 'Sending a design for review',
    kicker: 'Review',
    summary: 'Getting the dentist to approve the crown before it is milled.',
    steps: [
      {
        body: 'In NμCAD choose Send for Review. The dentist gets an email with a link that opens in any browser - no account and nothing to install. The link stays valid for 30 days.',
        note: 'Add a passcode (4 or more characters) when the case is identifiable, and tell the dentist the passcode separately. A forwarded email is then not access on its own.',
      },
      {
        body: 'The dentist turns the crown around, pins notes to points on it, and approves it, asks for changes or rejects it. The decision and notes come back to NμDent, and the Review Links page in this portal shows where each one stands.',
      },
      {
        body: 'Once it is approved, export the crown for your mill.',
        note: 'An approval belongs to the version the dentist saw. If you change the design afterwards, send it for review again.',
      },
    ],
  },
  {
    id: 'offline',
    number: 6,
    title: 'Working offline and updating',
    kicker: 'Offline and updates',
    summary: 'What NμDent does without an internet connection, and how new versions arrive.',
    steps: [
      {
        body: 'Without a connection NμDent opens with your PIN and keeps working for up to 7 days after it last reached our servers. Settings > Profile shows how many days are left; connecting again renews it on its own.',
      },
      {
        body: 'NμDent checks for a new version when it starts. Settings > Updates shows the version you have, anything newer and what changed. You choose when to install, except for an update marked as required.',
      },
    ],
  },
  {
    id: 'licensing',
    number: 7,
    title: 'Moving a seat to a new computer',
    kicker: 'Licensing',
    summary: 'Replacing or reinstalling the computer a seat belongs to.',
    before:
      'Each seat belongs to the computer it was first activated on. To use it on another computer, support first releases it from the old one.',
    steps: [
      {
        body: 'Raise an issue on the Issues page under Licensing, and say which computer the seat should leave.',
      },
      {
        body: 'Support releases the seat and you get an email saying your licence has been reset.',
      },
      {
        body: 'Install NμDent on the new computer (section 2) and activate it with the same email and licence key.',
        note: 'If NμDent notices changed hardware on its own, a Single Seat keeps working for 14 days while this is sorted out; a Lab Seat stops until support releases it.',
      },
    ],
  },
  {
    id: 'help',
    number: 8,
    title: 'Getting help',
    kicker: 'Support',
    summary: 'Where to ask, and where the answer comes back.',
    steps: [
      {
        body: 'Raise an issue on the Issues page, pick the category that fits and describe what happened. Support replies on the issue and by email.',
      },
      {
        body: 'Reply on the issue to add anything. If an issue marked resolved is still happening, reply there and it goes back to support.',
      },
      {
        body: 'If you cannot sign in at all, email helpdesk.nuvoai@gmail.com or call 1800-202-7201.',
      },
    ],
  },
];

export function ManualPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0]?.id ?? '');
  const active = SECTIONS.find((s) => s.id === activeId) ?? SECTIONS[0];

  return (
    <>
      <Meta title="User manual" />

      <Container className="grid gap-7 lg:grid-cols-[260px_1fr]">
        <nav aria-label="Manual contents" className="lg:sticky lg:top-0 lg:self-start">
          <p className="px-2.5 pb-2 font-mono text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
            Contents
          </p>
          <ul className="flex flex-col gap-0.5">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveId(section.id);
                  }}
                  aria-current={section.id === activeId ? 'true' : undefined}
                  className={cn(
                    'flex w-full items-baseline gap-2 rounded-[7px] px-2.5 py-[7px] text-left text-[12.5px] transition-colors',
                    section.id === activeId
                      ? 'bg-accent font-semibold text-accent-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <span className="font-mono text-[11px] opacity-60">{section.number}</span>
                  <span>{section.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {active && (
          <article className="min-w-0 rounded-xl border border-border bg-card px-[38px] py-8 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
            <p className="mb-2.5 font-mono text-[10.5px] tracking-[0.08em] text-brand-500 uppercase">
              Section {active.number} · {active.kicker}
            </p>
            <h2 className="mb-3.5 text-[25px] font-semibold tracking-[-0.6px]">{active.title}</h2>
            <p className="mb-4 max-w-[62ch] text-[13.5px] leading-[1.75] text-muted-foreground">
              {active.summary}
            </p>

            {active.before && (
              <div className="mb-[18px] max-w-[62ch] rounded-r-[9px] border-l-[3px] border-neutral-300 bg-muted/50 px-[18px] py-3.5">
                <p className="mb-1.5 text-xs font-semibold">Before you begin</p>
                <p className="text-[12.5px] leading-[1.65] text-muted-foreground">
                  {active.before}
                </p>
              </div>
            )}

            <ol className="max-w-[62ch] list-decimal space-y-2 pl-5 text-[13.5px] leading-[1.9] text-muted-foreground">
              {active.steps.map((step) => (
                <li key={step.body}>
                  <span className="text-foreground">{step.body}</span>
                  {step.note && (
                    <span className="mt-1.5 block border-l-2 border-border pl-3 text-[12.5px] leading-[1.65]">
                      {step.note}
                    </span>
                  )}
                </li>
              ))}
            </ol>

            <div className="mt-8 flex max-w-[62ch] flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-3.5">
              <p className="text-[12.5px] text-muted-foreground">
                Not working the way this describes?
              </p>
              <Link
                to={ROUTES.issues}
                className="text-[12.5px] font-semibold text-primary underline-offset-4 hover:underline"
              >
                Raise an issue →
              </Link>
            </div>
          </article>
        )}
      </Container>
    </>
  );
}
